import { Formik } from "formik";
import { FormikHelpers } from "formik/dist/types";
import React, { useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Feedback from "react-bootstrap/Feedback";
import * as yup from "yup";
import Widget from "../../../../forms/components/Widget";
import { onBulkSearchFn } from "../../@types";
import QuickSearchButtonsGroup from "./components/QuickSearchButtonsGroup";

interface FormValues {
  hashesUpload?: File;
  hashesText: string;
}

const initialValues: FormValues = {
  hashesUpload: undefined,
  hashesText: ""
};

const validationSchema = yup.object<FormValues>().shape({
  hashesUpload: yup
    .mixed<File>()
    .notRequired()
    .test({
      name: "fileSize",
      message: "Empty file detected!",
      test: value => {
        if (value) {
          return value.size > 0;
        }

        return true;
      }
    }),
  hashesText: yup.string().test({
    name: "hashesText-is-required-if-file-empty",
    message: "Enter or upload sha1, sha256 or md5 hashes!",
    test: (value, context) => {
      if (context.parent.hashesUpload) {
        return true;
      }

      return !!value;
    }
  })
});

interface IBulkSearchFormProps {
  onClose: () => void;
  onBulkSearch: (value: onBulkSearchFn) => void;
}

const BulkSearchView = ({ onBulkSearch, onClose }: IBulkSearchFormProps) => {
  const onFormSubmit = (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    if (values.hashesUpload) {
      const fileReader = new FileReader();
      fileReader.onload = event => {
        if (!event.target) {
          actions.setFieldError("hashesUpload", "Empty file detected!");
          return;
        }

        const hashesString = event.target.result as string;
        onBulkSearch({ content: hashesString });
        onClose();
      };

      fileReader.onerror = () => {
        fileReader.abort();
      };

      fileReader.onabort = () => {
        actions.setFieldError(
          "hashesUpload",
          "Oops! Seems like there was an error encountered while reading file. Upload aborted, please try again."
        );
      };

      fileReader.readAsText(values.hashesUpload);
    } else if (values.hashesText) {
      onBulkSearch({ content: values.hashesText });
      onClose();
    } else {
      actions.setFieldError(
        "hashesText",
        "Enter or upload sha1, sha256 or md5 hashes!"
      );
    }
  };

  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  return (
    <Formik
      onSubmit={onFormSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
    >
      {({
        handleSubmit,
        handleChange,
        handleReset,
        values,
        errors,
        setFieldValue
      }) => (
        <Form
          noValidate
          onSubmit={handleSubmit}
          className={"quick-search__form-container"}
        >
          <Row
            className={`align-center ${errors.hashesUpload ? "has-error" : ""}`}
          >
            <Col xs={5}>
              <Form.Control
                type={"file"}
                name={"hashesUpload"}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("hashesUpload", event.currentTarget.files?.[0]);
                }}
                isInvalid={!!errors.hashesUpload}
                ref={fileUploadRef}
                disabled={!!values.hashesText}
                data-testid={"bulk_search_modal_hashes_upload"}
              />
            </Col>
            <Col xs={7}>
              <div>
                <small style={{ color: "#999999" }}>
                  Upload a file containing sha1, sha256 and md5 hashes or enter
                  them into the textarea input box. Hashes should be separated
                  by either comma, space, or newline.
                </small>
              </div>
              <div>
                <small style={{ color: "#999999" }}>
                  <b>PLEASE NOTE</b>: Bulk hash search can take up to one
                  minute, depending on the number of hashes.
                </small>
              </div>
            </Col>
            <Feedback type="invalid">{errors.hashesUpload}</Feedback>
          </Row>

          <Widget
            as="textarea"
            vertical
            rows={errors.hashesUpload || errors.hashesText ? 12 : 14}
            name="hashesText"
            helpImportant="The maximum amount of hashes that can be submitted at once is 10 000."
            disabled={!!values.hashesUpload}
            data-testid={"bulk_search_modal_hashes_textarea"}
            onChange={handleChange}
            errors={errors.hashesText ? [errors.hashesText] : []}
            marginBottom={0}
            value={values.hashesText}
          />

          <QuickSearchButtonsGroup
            onSubmit={handleSubmit}
            onCancel={onClose}
            onClear={() => {
              handleReset();
              if (fileUploadRef.current) {
                fileUploadRef.current.value = "";
              }
            }}
            searchDisabled={
              Object.keys(errors).length > 0 ||
              (!values.hashesText && !values.hashesUpload)
            }
          />
        </Form>
      )}
    </Formik>
  );
};

export default BulkSearchView;
